<script setup lang="ts">
/**
 * CreateUserDrawer — drawer form for `POST /api/cms/users`.
 *
 * Fields: userName, password, external, groupId.
 *
 * Validation flow follows the useApiForm contract:
 *   1. Zod runs on every field change/blur. Inline errors render
 *      via FormRow's `:error` prop.
 *   2. On submit, schema runs once more. If clean, the API call
 *      goes out. ApiException.fieldErrors map back to inline errors;
 *      other errors fall through to useApiMutation's toast.
 *
 * The group picker is fed by useGroupsQuery — the new GET /groups
 * endpoint the backend team is adding for Batch A. Three states:
 *   - loading: select shows a spinner
 *   - empty:   inline message instructing admin to seed groups
 *   - data:    real options
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToastStore } from '@/stores/toast'
import Drawer from '@/components/ui/Drawer.vue'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import Select from '@/components/ui/Select.vue'
import Switch from '@/components/ui/Switch.vue'
import FormRow from '@/components/ui/FormRow.vue'
import { useApiForm } from '@/composables/useApiForm'
import { userCreateSchema } from '../schemas'
import { useCreateUserMutation, useGroupsQuery } from '../queries'

const open = defineModel<boolean>('open', { required: true })

const { t } = useI18n()
const toast = useToastStore()
const groupsQuery = useGroupsQuery()
const createUser = useCreateUserMutation()

const form = useApiForm({
  schema: userCreateSchema,
  initialValues: {
    userName: '',
    password: '',
    external: false,
    groupId: undefined as any,
  },
  submit: async (values) => {
    return createUser.mutateAsync(values)
  },
  onSuccess: () => {
    toast.push({
      kind: 'success',
      message: t('iam.create.success', { name: form.values.userName }),
    })
    open.value = false
  },
})

// Group select options. Returns an empty list while loading or on
// error — the EmptyState below the select handles those UX states.
const groupOptions = computed(() => {
  const groups = groupsQuery.data.value ?? []
  return groups.map((g) => ({
    value: String(g.id),
    label:
      g.memberCount !== undefined
        ? `${g.name} (${g.memberCount})`
        : g.name,
  }))
})

const showGroupsLoading = computed(
  () => groupsQuery.isLoading.value && !groupsQuery.data.value,
)
const showGroupsEmpty = computed(
  () =>
    !groupsQuery.isLoading.value &&
    (groupsQuery.error.value !== null ||
      (groupsQuery.data.value?.length ?? 0) === 0),
)

// vee-validate's defineField returns [Ref<value>, attrs] — we keep
// the value ref and bind it to v-model. Attrs are unused because our
// inputs handle their own onBlur/onInput via v-model.
const [userName] = form.defineField('userName')
const [password] = form.defineField('password')
const [external] = form.defineField('external')
const [groupId] = form.defineField('groupId')

/**
 * The Select component speaks string values; UserCreateInput.groupId
 * is number. Bridge with a computed setter that parses on the way in.
 */
const groupIdAsString = computed<string>({
  get: () => (groupId.value !== undefined ? String(groupId.value) : ''),
  set: (v: string) => {
    const n = Number.parseInt(v, 10)
    groupId.value = Number.isNaN(n) ? (undefined as unknown as number) : n
  },
})

function close(): void {
  open.value = false
  form.resetForm()
}
</script>

<template>
  <Drawer
    v-model:open="open"
    :title="$t('iam.create.title')"
    :subtitle="$t('iam.create.subtitle')"
    size="md"
  >
    <form @submit.prevent="form.submit()">
      <FormRow
        :label="$t('iam.create.userName.label')"
        :helper="$t('iam.create.userName.helper')"
        :error="form.errors.value.userName"
        required
      >
        <Input
          v-model="userName"
          type="text"
          :placeholder="$t('iam.create.userName.placeholder')"
          :error="form.errors.value.userName"
        />
      </FormRow>

      <FormRow
        :label="$t('iam.create.password.label')"
        :helper="$t('iam.create.password.helper')"
        :error="form.errors.value.password"
        required
      >
        <Input
          v-model="password"
          type="password"
          :placeholder="$t('iam.create.password.placeholder')"
          :error="form.errors.value.password"
        />
      </FormRow>

      <FormRow
        :label="$t('iam.create.group.label')"
        :error="form.errors.value.groupId"
        required
      >
        <div v-if="showGroupsLoading" class="text-xs text-text-4">
          {{ $t('iam.create.group.loading') }}
        </div>
        <div v-else-if="showGroupsEmpty" class="text-xs text-amber">
          {{ $t('iam.create.group.empty') }}
        </div>
        <Select
          v-else
          v-model="groupIdAsString"
          :options="groupOptions"
          :placeholder="$t('iam.create.group.placeholder')"
          :error="form.errors.value.groupId"
        />
      </FormRow>

      <FormRow
        :label="$t('iam.create.external.label')"
        :helper="$t('iam.create.external.helper')"
        inline
      >
        <Switch :model-value="external ?? false" @update:model-value="val => external = val" />
      </FormRow>
    </form>

    <template #footer>
      <Button kind="ghost" @click="close">
        {{ $t('actions.cancel') }}
      </Button>
      <Button
        kind="primary"
        type="submit"
        :loading="form.isSubmitting.value"
        :disabled="showGroupsEmpty"
        @click="form.submit()"
      >
        {{ $t('iam.create.submit') }}
      </Button>
    </template>
  </Drawer>
</template>
